<template>
  <div>
    <!-- framed: na rota a ficha ganha a moldura do item. No modal não — o card do diálogo já é
         uma, e duas seriam moldura dupla. -->
    <ontos-instance-view
        ref="view"
        :key="contentKey"
        framed
        :context="context"
        :workspace="workspace"
        @open-instance="onOpenInstance"
        @edit="onEdit"
        @changed="onChanged($event)"
        @deleted="$router.push({name: 'list-instances', params: $route.params})"
        @not-found="$router.push({name: 'home'})"
        @crumb="$emit('crumb', $event)"
        @showMessage="$emit('showMessage', $event)"
        @showErrorMessage="$emit('showErrorMessage', $event)"
    >
    </ontos-instance-view>

    <!-- O item excluído na ficha de cima estava numa lista desta (um relacionamento), e sai dela sem
         reler — ver o `removeDeleted` da ficha. Sem isto, a exclusão fechava a janela e a lista de
         trás continuava mostrando o item (relato do usuário). -->
    <ontos-view-instance-dialog
        v-if="instanceOnView"
        :context="instanceOnView"
        :preview="previewOnView"
        :workspace="workspace"
        @changed="reloadNonce++"
        @deleted="onChildDeleted"
        @closed="instanceOnView = null"
        @showMessage="$emit('showMessage', $event)"
        @showErrorMessage="$emit('showErrorMessage', $event)"
    />
  </div>
</template>
<script lang="ts">
import {Component, toNative} from 'vue-facing-decorator'
import IdeaLevelRouteComponent from "@/components/shared/IdeaLevelRouteComponent";
import InstanceView from "@/components/live/instance/InstanceView.vue";
import ViewInstanceDialog from "@/components/live/instance/ViewInstanceDialog.vue";
import {InstanceContext} from "@/types/InstanceContext";
import {instanceContentKey, instanceKey, instanceRoute, isInPlace} from "@/utils/instanceLink";
import {handOver} from "@/utils/handover";
import {Preview} from "@/utils/instancePreview";

/**
 * Página da rota view-instance. A tela em si vive em InstanceView.
 *
 * Aqui a JANELA é a rota, e é isso que decide o que cada clique faz: item de lista ou card de
 * Kanban abre um modal por cima — dali se espia e se volta —, e link de dentro do item NAVEGA,
 * trocando a URL. Antes tudo abria modal, e a rota nunca mudava.
 *
 * Editar segue a mesma regra: na rota, a janela é a URL, então o lápis vai para /edit — levando
 * junto o que a ficha já carregou, para a página de edição não reler o que está na memória.
 */
@Component({
  emits: ['crumb'],
  components: {
    'ontos-instance-view': InstanceView,
    'ontos-view-instance-dialog': ViewInstanceDialog
  }
})
class ViewInstance extends IdeaLevelRouteComponent {
  instanceOnView: InstanceContext | null = null
  /** O que o item aberto por cima já tem — a linha da tabela de onde se clicou. */
  previewOnView: Preview | null = null
  reloadNonce = 0

  get context(): InstanceContext {
    const {system, idea, instance} = this.params
    return {system, idea, instance}
  }

  /**
   * O clique numa outra instância: empilha ou navega.
   *
   * Handler de verdade, e não `$event` no template, porque agora os DOIS argumentos importam —
   * no template só chega o primeiro. O desenho de "um evento com segundo argumento" nasceu
   * justamente porque a rota podia ignorar o segundo; deixou de poder.
   *
   * `over` vem de uma lista ou de um Kanban (linha de tabela, card): ali a ficha abre por cima e
   * fechar devolve a lista. Sem ele é navegação dentro do item — um chip, um campo —, e na rota
   * navegar quer dizer trocar a URL, com o voltar do navegador funcionando.
   */
  /**
   * Editar na rota é navegar — e a ficha vai junto.
   *
   * O que ela carregou é exatamente o que a edição precisaria reler, inclusive os candidatos
   * dos relacionamentos (`rel-domains`), que são a parte cara. A entrega vale só para a próxima
   * tela e some ao ser usada; quem chegar em /edit por URL, F5 ou voltar não a encontra e lê do
   * servidor, como sempre.
   */
  onEdit(editing: any) {
    handOver(instanceKey(this.context), editing)
    this.$router.push({name: 'edit-instance', params: this.params}).catch(() => undefined)
  }

  onOpenInstance(context: InstanceContext, over = false, preview?: Preview) {
    if (over) {
      this.instanceOnView = context
      this.previewOnView = preview || null
    } else this.$router.push(instanceRoute(context)).catch(() => undefined)
  }

  get contentKey() {
    return instanceContentKey(this.context, this.reloadNonce)
  }

  /** A ficha mudou: relê remontando — a não ser que ela já tenha aplicado em si (`IN_PLACE`). */
  onChanged(change?: unknown) {
    if (!isInPlace(change)) this.reloadNonce++
  }

  /** O item excluído na ficha de cima sai das listas desta, sem reler — ver o `removeDeleted`. */
  onChildDeleted(id: number | string, idea: string) {
    (this.$refs.view as any)?.removeDeleted(idea, id)
  }
}

export default toNative(ViewInstance)
</script>
